/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import tw from 'twin.macro';

import { floor, isEmpty, isNaN, isNil, round, toNumber } from 'lodash';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { RootState } from 'reducers';
import { IStartEnd } from 'utils/models';
import Wavesurfer from 'wavesurfer.js';
// @ts-ignore
import CursorPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.cursor';
// @ts-ignore
import RegionPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions';
import { Region, RegionsPluginParams } from 'wavesurfer.js/src/plugin/regions';
import { WaveSurferParams } from 'wavesurfer.js/types/params';
import {
  downsamplePeaksToBucketPairs,
  getTargetWaveformBarPairs,
} from 'utils/waveformPeaks.util';

const finiteMediaDuration = (
  video: HTMLMediaElement | null | undefined,
): number | null => {
  if (!video) return null;
  const duration = video.duration;
  return Number.isFinite(duration) && duration > 0 ? duration : null;
};

const clampMediaSeekTime = (
  time: number,
  duration: number,
): number | null => {
  if (!Number.isFinite(time) || time < 0) return null;
  if (!Number.isFinite(duration) || duration <= 0) return null;
  return Math.min(time, duration);
};

interface IProps {
  src: string;
  currentTime?: IStartEnd | null;
  defaultTime?: IStartEnd | null;
  progressColor?: string;
  cursorColor?: string;
  shouldReset?: boolean;
  shouldZoomFocus?: boolean;
  zoomLevel?: number;
  setZoomLevel?: (zoom: number) => unknown;
  shouldHideRegion?: boolean;
  onRegionUpdate?: (start: number, end: number) => unknown;
  customParams?: Partial<WaveSurferParams>;
}

/**
 * Clip-region UI (Publish, TimeCodeChapterModal): pass `currentTime` + visible region.
 * Visual playbar only (MediaTimeTrack / DWCM): pass `currentTime={null}` and
 * `shouldHideRegion={true}` — must not seek the media element or pause on region-out;
 * DWCM owns seeks via progress bar + TranscriptChunkStore.
 */
const isClipRegionMode = (props: IProps) =>
  !isEmpty(props.currentTime) && !props.shouldHideRegion;

const computeClipZoom = (startTime: number, endTime: number) => {
  const regionTime = endTime - startTime;
  const TOTAL_PIXELS = 615;
  let needToZoom = (TOTAL_PIXELS / regionTime) * 0.9;
  if (needToZoom > 100) {
    needToZoom = 100;
  }
  return round(needToZoom);
};

export const WaveSurferVideo = forwardRef((props: IProps, videoRef: any) => {
  // console.count('==== WaveSurferVideo');

  const clipRegionMode = isClipRegionMode(props);

  const [isLoaded, toggleLoaded] = useToggle(false);
  const [isReady, toggleReady] = useToggle(false);
  const [videoDuration, setVideoDuration] = useState<number>();

  const media = useSelector((state: RootState) => state.media);

  const wavesurferRef = useRef(null);
  const wavesurferInstanceRef = useRef<any>(null);
  const pendingClipSeekRef = useRef<number | null>(null);
  const clipBoundsRef = useRef({ start: 0, end: 0 });
  const lastSyncedStartRef = useRef<number | null>(null);
  const userChosePlayheadRef = useRef(false);
  const playbackStartedBeforeEndRef = useRef(false);
  const programmaticSeekRef = useRef(false);
  const regionDragActiveRef = useRef(false);
  const regionDragEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const onRegionUpdateRef = useRef(props.onRegionUpdate);
  onRegionUpdateRef.current = props.onRegionUpdate;
  const programmaticRegionUpdateRef = useRef(false);

  const applyClipRegionUpdate = (
    region: Region,
    updates: { start?: number; end?: number },
  ) => {
    programmaticRegionUpdateRef.current = true;
    region.update({ id: '1', ...updates });
    requestAnimationFrame(() => {
      programmaticRegionUpdateRef.current = false;
    });
  };

  const getClipRegion = () => {
    const list = wavesurferInstanceRef.current?.regions?.list;
    if (!list) return null;
    return list['1'] ?? list[1] ?? null;
  };

  const pauseClipAtEnd = (endTime: number) => {
    const video = videoRef?.current as HTMLMediaElement | undefined;
    const wavesurfer = wavesurferInstanceRef.current;
    const duration = finiteMediaDuration(video) ?? endTime;
    const clampedEnd = clampMediaSeekTime(endTime, duration) ?? endTime;

    if (video && !video.paused) {
      video.pause();
    }
    wavesurfer?.pause?.();
    wavesurfer?.backend?.clearPlayEnd?.();

    if (video) {
      try {
        video.currentTime = clampedEnd;
      } catch {
        // ignore seek failures at clip end
      }
    }

    if (wavesurfer?.isReady) {
      try {
        wavesurfer.seekTo(clampedEnd / wavesurfer.getDuration());
      } catch {
        // ignore drawer sync failures
      }
    }

    updateClipCursorLabel(clampedEnd);
    playbackStartedBeforeEndRef.current = false;
  };

  const markUserPlayheadChoice = () => {
    if (programmaticSeekRef.current) return;
    userChosePlayheadRef.current = true;
  };

  const armClipEndPlayback = () => {
    const video = videoRef?.current as HTMLMediaElement | undefined;
    const { end: endTime } = clipBoundsRef.current;
    const backend = wavesurferInstanceRef.current?.backend;

    if (!video || !Number.isFinite(endTime) || endTime <= 0) return;

    // Only stop at clip end when playing forward from before the end boundary.
    if (video.currentTime >= endTime - 0.04) {
      backend?.clearPlayEnd?.();
      return;
    }

    backend?.setPlayEnd?.(endTime);
  };

  const timeToCursorX = (wavesurfer: any, time: number) => {
    const duration = wavesurfer.getDuration();
    if (!Number.isFinite(duration) || duration <= 0) return 0;

    const drawer = wavesurfer.drawer;
    const elementWidth = drawer.width / wavesurfer.params.pixelRatio;
    const scrollWidth = drawer.getScrollX?.() ?? drawer.wrapper?.scrollLeft ?? 0;
    const scrollTime = (duration / drawer.width) * scrollWidth;

    return Math.max(0, ((time - scrollTime) / duration) * elementWidth);
  };

  const updateClipCursorLabel = (time: number) => {
    const wavesurfer = wavesurferInstanceRef.current;
    const cursor = wavesurfer?.cursor;
    if (!cursor?.updateCursorPosition) return;

    cursor.showCursor();
    cursor.updateCursorPosition(timeToCursorX(wavesurfer, time), 0, false);
  };

  const syncClipPlayheadAndCursor = (time: number) => {
    const wavesurfer = wavesurferInstanceRef.current;
    const mediaDuration =
      finiteMediaDuration(videoRef?.current) ??
      (wavesurfer?.getDuration?.() > 0 ? wavesurfer.getDuration() : null);
    const seekTime = clampMediaSeekTime(time, mediaDuration ?? 0);

    if (seekTime == null) {
      pendingClipSeekRef.current = time;
      return;
    }

    pendingClipSeekRef.current = null;

    programmaticSeekRef.current = true;

    if (wavesurfer?.isReady && wavesurfer.getDuration() > 0) {
      try {
        wavesurfer.seekTo(seekTime / wavesurfer.getDuration());
        updateClipCursorLabel(seekTime);
        requestAnimationFrame(() => {
          programmaticSeekRef.current = false;
        });
        return;
      } catch {
        programmaticSeekRef.current = false;
        // fall through to direct media seek
      }
    } else {
      pendingClipSeekRef.current = seekTime;
    }

    if (videoRef?.current) {
      try {
        videoRef.current.currentTime = seekTime;
      } catch {
        // ignore seek before media metadata is ready
      }
    }

    requestAnimationFrame(() => {
      programmaticSeekRef.current = false;
    });
  };

  const flushPendingClipSeek = () => {
    const pending =
      pendingClipSeekRef.current ?? props.currentTime?.startTime ?? null;
    if (pending == null || !clipRegionMode) return;
    syncClipPlayheadAndCursor(pending);
    focusClipRegion(true);
  };

  const focusClipRegion = (smooth = false) => {
    if (!clipRegionMode) return;
    const region = (wavesurferRef.current as HTMLElement | null)?.querySelector(
      'region.wavesurfer-region',
    );
    region?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      inline: 'center',
      block: 'nearest',
    });
  };

  const getBucketPeaksForContainer = (peaks: any) => {
    const barWidth = props?.customParams?.barWidth ?? 2;
    const barGap = props?.customParams?.barGap ?? 0;
    const containerWidth =
      (wavesurferRef.current as HTMLElement | null)?.clientWidth ?? 0;
    const targetPairs = getTargetWaveformBarPairs(
      containerWidth,
      barWidth,
      barGap,
    );
    return downsamplePeaksToBucketPairs(peaks, targetPairs);
  };

  useEffect(() => {
    if (
      isNil(videoRef?.current?.duration) ||
      isNaN(videoRef?.current?.duration)
    )
      return;

    const totalDuration = floor(videoRef?.current?.duration, 3);
    setVideoDuration(totalDuration);

    if (!props?.currentTime || !clipRegionMode) return;
    const { startTime, endTime } = props.currentTime;

    const regionTime = endTime - startTime;
    const TOTAL_PIXELS = 615;
    if (regionTime / TOTAL_PIXELS > 0.9 || regionTime >= totalDuration) return;

    const needToZoom = computeClipZoom(startTime, endTime);

    wavesurferInstanceRef.current?.zoom(needToZoom);
    props?.setZoomLevel?.(needToZoom);
    requestAnimationFrame(() => focusClipRegion(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef?.current?.duration]);

  useEffect(() => {
    if (!props?.src) return;

    if (wavesurferRef.current && videoRef?.current) {
      const wavesurfer = Wavesurfer.create({
        container: wavesurferRef.current as any,
        backend: 'MediaElement',
        height: 40,
        barWidth: 2,
        barRadius: 2,
        cursorWidth: 2,
        // barGap: 1,
        barGap: 0,
        cursorColor: props?.cursorColor ?? '#E7483D',
        waveColor: 'white',
        // progressColor: props?.progressColor ?? '#555',
        progressColor: props?.progressColor ?? '#bd181c',
        // normalize: true,
        responsive: true,
        pixelRatio: 1,
        scrollParent: true,
        autoCenterRate: 10,
        minPxPerSec: 1,
        plugins: [
          RegionPlugin.create({
            regions: [
              {
                id: '1',
                start: 0,
                // end: 60,
                color: 'transparent',
                preventContextMenu: true,
                drag: false,
                resize: true,
                // @ts-ignore
                minLength: 1,
              },
            ],
          } as RegionsPluginParams),
          CursorPlugin.create({
            showTime: true,
            hideOnBlur: !clipRegionMode,
            opacity: 1,
            customShowTimeStyle: {
              'font-size': '1.1rem',
              color: '#FEB446',
              padding: '4px',
              'font-weight': 500,
              'margin-top': '-22px',
            },
          }),
        ],
        ...props?.customParams,
      });

      // Load from v2.json otherwise load default by WaveSurfer
      if (videoRef?.current) {
        const bucketPeaks = getBucketPeaksForContainer(media?.waveform?.data);
        wavesurfer.load(videoRef?.current, bucketPeaks || undefined);
      }

      wavesurferInstanceRef.current = wavesurfer;

      wavesurfer.on('ready', () => {
        (window as any).WaveSurfer = wavesurfer;

        toggleLoaded(true);
        toggleReady(true);
      });

      // IMPORTANT: Turn on destroy() may break MediaControl.tsx after changing version
      // return () => wavesurfer.destroy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.src]);

  useEffect(() => {
    if (!wavesurferInstanceRef.current || isEmpty(media?.waveform?.data))
      return;

    const bucketPeaks = getBucketPeaksForContainer(media.waveform!.data);
    if (!bucketPeaks) return;

    wavesurferInstanceRef.current.backend.setPeaks(bucketPeaks);
    wavesurferInstanceRef.current.drawBuffer?.();
  }, [media?.waveform?.data]);

  useEffect(() => {
    if (!isReady || !clipRegionMode) return;

    const wavesurfer = wavesurferInstanceRef.current;
    if (!wavesurfer) return;

    const clipTime = props.currentTime;
    const region = getClipRegion();
    if (clipTime && region) {
      applyClipRegionUpdate(region, {
        start: clipTime.startTime,
        end: clipTime.endTime,
      });
    }
    flushPendingClipSeek();

    const onRegionUpdated = (region: Region) => {
      if (programmaticRegionUpdateRef.current) return;

      regionDragActiveRef.current = true;
      if (regionDragEndTimerRef.current) {
        clearTimeout(regionDragEndTimerRef.current);
      }
      regionDragEndTimerRef.current = setTimeout(() => {
        regionDragActiveRef.current = false;
        regionDragEndTimerRef.current = null;
        focusClipRegion(true);
      }, 120);

      clipBoundsRef.current = {
        start: region.start,
        end: region.end,
      };
      onRegionUpdateRef.current?.(region.start, region.end);

      if (wavesurfer?.cursor?.params?.showTime === true) {
        wavesurfer.cursor.hideCursor();
      }
    };

    const onRegionOut = () => {
      const video = videoRef?.current as HTMLMediaElement | undefined;
      if (!video || video.paused || !playbackStartedBeforeEndRef.current) {
        return;
      }
      pauseClipAtEnd(clipBoundsRef.current.end);
    };

    const onRegionMouseLeave = () => {
      wavesurfer.cursor.showCursor();
    };

    wavesurfer.on('interaction', markUserPlayheadChoice);
    wavesurfer.on('seek', markUserPlayheadChoice);
    wavesurfer.on('region-updated', onRegionUpdated);
    wavesurfer.on('region-out', onRegionOut);
    wavesurfer.on('region-mouseleave', onRegionMouseLeave);

    return () => {
      wavesurfer.un('interaction', markUserPlayheadChoice);
      wavesurfer.un('seek', markUserPlayheadChoice);
      wavesurfer.un('region-updated', onRegionUpdated);
      wavesurfer.un('region-out', onRegionOut);
      wavesurfer.un('region-mouseleave', onRegionMouseLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, clipRegionMode]);

  useEffect(() => {
    if (!clipRegionMode || !wavesurferInstanceRef?.current?.regions) return;

    const region = getClipRegion();
    if (!region) return;

    applyClipRegionUpdate(region, {
      start: props?.defaultTime?.startTime,
      end: props?.defaultTime?.endTime,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.shouldReset, clipRegionMode]);

  useEffect(() => {
    if (
      !clipRegionMode ||
      !wavesurferInstanceRef?.current?.regions ||
      !videoRef?.current ||
      !props?.currentTime ||
      !videoDuration
    )
      return;

    const { startTime, endTime } = props.currentTime;

    const needToZoom = computeClipZoom(startTime, endTime);

    wavesurferInstanceRef.current.zoom(needToZoom);
    props?.setZoomLevel?.(needToZoom);
    requestAnimationFrame(() => focusClipRegion(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.shouldZoomFocus]);

  useEffect(() => {
    if (!clipRegionMode || props.currentTime == null || isEmpty(props.currentTime))
      return;
    clipBoundsRef.current = {
      start: props.currentTime.startTime,
      end: props.currentTime.endTime,
    };
  }, [props?.currentTime?.startTime, props?.currentTime?.endTime, clipRegionMode]);

  useEffect(() => {
    if (!clipRegionMode || !isReady) return;

    const video = videoRef?.current as HTMLMediaElement | undefined;
    if (!video) return;

    const onPlay = () => {
      const { end } = clipBoundsRef.current;
      playbackStartedBeforeEndRef.current =
        video.currentTime < end - 0.04;
      armClipEndPlayback();
    };

    const onTimeUpdate = () => {
      if (video.paused || !playbackStartedBeforeEndRef.current) return;

      const { end: endTime } = clipBoundsRef.current;
      if (!Number.isFinite(endTime) || endTime <= 0) return;

      if (video.currentTime >= endTime - 0.04) {
        pauseClipAtEnd(endTime);
      }
    };

    const onSeeked = () => {
      markUserPlayheadChoice();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('seeked', onSeeked);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [clipRegionMode, isReady, videoRef?.current]);

  useEffect(() => {
    if (!clipRegionMode || !isReady) return;
    const video = videoRef?.current as HTMLMediaElement | undefined;
    if (video && !video.paused) {
      armClipEndPlayback();
    }
  }, [props?.currentTime?.endTime, clipRegionMode, isReady]);

  useEffect(() => {
    if (!clipRegionMode || props.currentTime == null || isEmpty(props.currentTime))
      return;
    if (regionDragActiveRef.current) return;

    const clipTime = props.currentTime;
    const region = getClipRegion();
    if (!region) return;

    const startTime = toNumber(clipTime.startTime);
    const endTime = toNumber(clipTime.endTime);
    if (!Number.isFinite(startTime) || startTime < 0) return;

    const startMatches = Math.abs(region.start - startTime) < 0.001;
    const endMatches = Math.abs(region.end - endTime) < 0.001;
    if (startMatches && endMatches) return;

    const updates: { start?: number; end?: number } = {};
    if (!startMatches) updates.start = startTime;
    if (!endMatches) updates.end = endTime;
    applyClipRegionUpdate(region, updates);
  }, [
    props?.currentTime?.startTime,
    props?.currentTime?.endTime,
    clipRegionMode,
    isReady,
  ]);

  useEffect(() => {
    if (!clipRegionMode || props.currentTime == null || isEmpty(props.currentTime))
      return;
    if (regionDragActiveRef.current) return;

    const clipTime = props.currentTime;

    if (!isReady) {
      pendingClipSeekRef.current = clipTime.startTime;
      return;
    }

    const startTime = toNumber(clipTime.startTime);
    const startChanged = lastSyncedStartRef.current !== startTime;

    if (!startChanged && userChosePlayheadRef.current) {
      return;
    }

    lastSyncedStartRef.current = startTime;
    userChosePlayheadRef.current = false;
    syncClipPlayheadAndCursor(startTime);
    focusClipRegion(false);
  }, [props?.currentTime?.startTime, clipRegionMode, isReady]);

  useEffect(() => {
    if (isNil(props.zoomLevel) || !wavesurferInstanceRef.current) return;

    wavesurferInstanceRef.current.zoom(round(props.zoomLevel));
  }, [props?.zoomLevel]);

  return (
    <div
      css={[
        wavesurferStyles,
        (!isLoaded ||
          !isReady ||
          !Number.isFinite(videoDuration) ||
          props?.shouldHideRegion ||
          isEmpty(videoRef?.current) ||
          isEmpty(wavesurferRef?.current)) &&
          hiddenRegion,
        (!isReady || !Number.isFinite(videoDuration)) && tw`pointer-events-none`,
        toNumber(props?.zoomLevel) > 0 && cursorShorter,
      ]}
      ref={wavesurferRef}
    ></div>
  );
});

const hiddenRegion = css`
  region.wavesurfer-region {
    ${tw`hidden`}
  }
`;

const wavesurferStyles = css`
  ${tw`relative cursor-pointer`}

  region.wavesurfer-region {
    ${tw`
      relative
      border-solid
      // border-sonnant-red
      border-sonnant-orange
      border-width[3px 1rem]
      rounded
      z-index[10]!
    `}

    handle.wavesurfer-handle {
      ${tw`(background[unset] width[1rem])!`}

      &::before, &::after {
        content: '';
        position: absolute;
        border: 1px solid black;
        border-top: none;
        border-bottom: none;
        width: 3px;
        height: 60%;
        top: 20%;
        left: 4px;
      }

      &.wavesurfer-handle-start {
        left: -1rem !important;
      }
      &.wavesurfer-handle-end {
        right: -1rem !important;
      }
    }
  }

  wave {
    scrollbar-width: thin;
    @-moz-document url-prefix() {
      height: 35px !important;
    }

    /* wave:nth-of-type(1) {
      &::before {
        position: absolute;
        width: 8px;
        height: 8px;
        border: 1px solid white !important;
        border-radius: 50%;
        background-color: #5551ff;
        content: '';
        right: -2px;
        top: 0;
      }
    } */
  }
`;

const cursorShorter = css`
  cursor {
    bottom: 5px !important;
  }
`;
